/**
 * Kernels — detail-only page. The kernel inventory now lives in the
 * Studio sidebar (StudioMenu), so this page just renders the detail
 * body for whichever kernel is selected via `?kernel=<command>`. When
 * nothing is selected the page falls back to the StudioOverview
 * library landing.
 *
 * Deep link: `?view=kernels&kernel=<command>` selects the detail.
 * Used by the chat-canvas "↗ view kernel" affordance and by the
 * sidebar's ItemRow clicks.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ConfirmDialog from "../components/ConfirmDialog.jsx";
import SecretFindingOverrideDialog from "../components/SecretFindingOverrideDialog.jsx";
import KernelWidget from "../components/KernelWidget.jsx";
import KernelEditor from "../components/KernelEditor.jsx";
import KernelChat from "../components/KernelChat.jsx";
import ExportableBlock from "../components/ExportableBlock.jsx";
import HiddenOriginDeploymentViews from "../components/HiddenOriginDeploymentViews.jsx";
import StudioOverview from "../components/StudioOverview.jsx";
import SplitPage from "../components/SplitPage.jsx";
import ScrollArea from "../components/ScrollArea.jsx";
import TabStrip from "../components/TabStrip.jsx";
import TenantViewLifecycleNotice from "../components/TenantViewLifecycleNotice.jsx";
import { NOTE_CLASS } from "../lib/ui.js";
import { useDebouncedValue } from "../lib/useDebouncedValue.js";
import {
    listKernels,
    kernelResolverForTenant,
    saveKernelCloudFirst,
    deleteKernel,
    forkKernelCloudFirst,
    isBuiltinKernel,
    subscribeKernels,
} from "../lib/kernelStore.js";
import {
    findCanvasesUsingKernel,
    listCanvasTemplates,
    subscribeCanvasTemplates,
} from "../lib/canvasTemplateStore.js";
import {
    deleteCloudTenantView,
    forkCloudTenantView,
    hideCloudTenantView,
    isStoredTenantViewBody,
    listAllCloudTenantViewDependents,
    loadTenantViewBody,
    publishCloudTenantView,
    tenantViewDefinitionDiffers,
    unpublishCloudTenantView,
    updateCloudTenantView,
} from "../lib/tenantViewCloudStore.js";
import {
    RUN_SURFACES,
    createBundledRunContext,
    createLiveRunContext,
    createStoredRunContext,
    createUnknownRunContext,
    mapResolvedKernel,
    taintRunContext,
} from "../lib/kernelResolver.js";
import {
    KernelPublishabilityError,
    assertKernelScopePublishable,
} from "../lib/kernelPublishability.js";
import {
    formatKernelShareWarnings,
    kernelShareWarnings,
} from "../lib/kernelShareSafety.js";
import {
    inspectTenantViewSecretFindings,
    recordTenantViewRun,
} from "../lib/tenantViewsApi.js";
import { DIALOG } from "../lib/analytics/surfaceVocabulary.js";

const SERVER_KERNEL_FIELDS = Object.freeze([
    "visibility",
    "version",
    "createdBy",
    "created_by",
    "objectOrigin",
    "object_origin",
    "forkedFrom",
    "forked_from",
    "canEdit",
    "can_edit",
    "canDelete",
    "can_delete",
    "canFork",
    "can_fork",
    "canPublish",
    "can_publish",
    "canOverrideSecrets",
    "can_override_secrets",
    "canUnpublish",
    "can_unpublish",
]);

function hasOwn(value, key) {
    return value != null && Object.prototype.hasOwnProperty.call(value, key);
}

function readBoolean(value, camel, snake) {
    if (hasOwn(value, camel)) return value[camel] === true;
    if (hasOwn(value, snake)) return value[snake] === true;
    return false;
}

function lifecycleFailure(action, error) {
    const detail = error?.message || String(error || "Unknown error");
    return `${action} failed: ${detail}`;
}

/**
 * Normalize the server's per-row action metadata into the one shape consumed
 * by the page. The booleans are deliberately strict: absent or malformed
 * server metadata cannot turn a tenant-published row into an editable one.
 *
 * Legacy localStorage rows predate these fields, so they retain the page's
 * existing editable/deletable behavior until cloud import replaces them.
 * Bundled definitions remain read-only and forkable regardless of body fields.
 */
export function getKernelAffordances(kernel) {
    const isBuiltin = kernel?.builtin === true || isBuiltinKernel(kernel?.command);
    const isServerShaped = SERVER_KERNEL_FIELDS.some((key) => hasOwn(kernel, key));
    const hasForkDecision = hasOwn(kernel, "canFork") || hasOwn(kernel, "can_fork");

    if (isBuiltin) {
        return {
            isBuiltin: true,
            canEdit: false,
            canDelete: false,
            canFork: hasForkDecision ? readBoolean(kernel, "canFork", "can_fork") : true,
            canPublish: false,
        };
    }

    if (!isServerShaped) {
        return {
            isBuiltin: false,
            canEdit: true,
            canDelete: true,
            canFork: false,
            canPublish: false,
        };
    }

    return {
        isBuiltin: false,
        canEdit: readBoolean(kernel, "canEdit", "can_edit"),
        canDelete: readBoolean(kernel, "canDelete", "can_delete"),
        canFork: readBoolean(kernel, "canFork", "can_fork"),
        canPublish: readBoolean(kernel, "canPublish", "can_publish"),
    };
}

export default function KernelsPage({ selectedItem, onSelectItem, focusHeader }) {
    const [kernels, setKernels] = useState(() => listKernels(null));
    useEffect(() => subscribeKernels(() => setKernels(listKernels(null))), []);
    const [canvases, setCanvases] = useState(() => listCanvasTemplates(null));
    useEffect(() => subscribeCanvasTemplates(() => setCanvases(listCanvasTemplates(null))), []);

    // selectedItem comes from App's unified-tab state (the command of
    // the active "kernel:" tab). onSelectItem opens/activates a
    // typed tab — same function the Studio sidebar uses.
    const selectedCommand = selectedItem || null;
    const setSelectedCommand = useCallback((command) => {
        onSelectItem?.(command ? "kernels" : null, command || null);
    }, [onSelectItem]);
    const navigateTo = useCallback((view, itemId) => {
        onSelectItem?.(view, itemId);
    }, [onSelectItem]);

    const selectedKernel = useMemo(
        () => kernels.find((k) => k.command === selectedCommand) || null,
        [kernels, selectedCommand],
    );
    const [lifecycleNotice, setLifecycleNotice] = useState(null);
    const [pendingSecretPublish, setPendingSecretPublish] = useState(null);
    const lifecycleBusyRef = useRef(null);
    const [lifecycleBusy, setLifecycleBusy] = useState(null);
    const beginLifecycle = useCallback((action) => {
        if (lifecycleBusyRef.current) return false;
        lifecycleBusyRef.current = action;
        setLifecycleBusy(action);
        return true;
    }, []);
    const finishLifecycle = useCallback(() => {
        lifecycleBusyRef.current = null;
        setLifecycleBusy(null);
    }, []);
    const [bodyLoadError, setBodyLoadError] = useState(null);
    useEffect(() => {
        setBodyLoadError(null);
        if (!selectedKernel?.tenantViewId || selectedKernel.bodyLoaded === true) return undefined;
        const controller = new AbortController();
        loadTenantViewBody(selectedKernel.tenantViewId, { signal: controller.signal })
            .catch((error) => {
                if (error?.name !== "AbortError") setBodyLoadError(error);
            });
        return () => controller.abort();
    }, [selectedKernel?.bodyLoaded, selectedKernel?.tenantViewId]);

    // Delete confirm — the kernel might still be referenced by saved
    // canvases, so we surface that in the dialog body before destruction.
    const [pendingDelete, setPendingDelete] = useState(null);
    const [remoteUsagePreflight, setRemoteUsagePreflight] = useState({
        targetId: null,
        status: "idle",
        dependents: [],
        error: null,
    });
    const [preflightAttempt, setPreflightAttempt] = useState(0);
    const pendingTarget = useMemo(
        () => kernels.find((kernel) => kernel.command === pendingDelete) || null,
        [kernels, pendingDelete],
    );
    const remotePreflightIsCurrent = Boolean(
        pendingTarget?.tenantViewId
        && remoteUsagePreflight.targetId === pendingTarget.tenantViewId,
    );
    const remotePreflightReady = Boolean(
        remotePreflightIsCurrent && remoteUsagePreflight.status === "ready",
    );
    const pendingUsage = useMemo(() => {
        if (!pendingDelete) return [];
        return pendingTarget?.tenantViewId
            ? (remotePreflightReady ? remoteUsagePreflight.dependents : [])
            : findCanvasesUsingKernel(null, pendingDelete);
    }, [
        pendingDelete,
        pendingTarget,
        remotePreflightReady,
        remoteUsagePreflight.dependents,
    ]);
    useEffect(() => {
        if (!pendingTarget?.tenantViewId) {
            setRemoteUsagePreflight({
                targetId: null,
                status: "idle",
                dependents: [],
                error: null,
            });
            return;
        }
        const targetId = pendingTarget.tenantViewId;
        const controller = new AbortController();
        setRemoteUsagePreflight({
            targetId,
            status: "loading",
            dependents: [],
            error: null,
        });
        void (async () => {
            const dependents = await listAllCloudTenantViewDependents(pendingTarget, {
                pageSize: 200,
                signal: controller.signal,
            });
            if (!controller.signal.aborted) {
                setRemoteUsagePreflight({
                    targetId,
                    status: "ready",
                    dependents,
                    error: null,
                });
            }
        })().catch((error) => {
            if (controller.signal.aborted) return;
            setRemoteUsagePreflight({
                targetId,
                status: "error",
                dependents: [],
                error,
            });
        });
        return () => controller.abort();
    }, [pendingTarget, preflightAttempt]);
    const confirmDelete = useCallback(async () => {
        const command = pendingDelete;
        if (!command) return;
        const target = kernels.find((kernel) => kernel.command === command);
        if (!target || !getKernelAffordances(target).canDelete) {
            setPendingDelete(null);
            return;
        }
        if (target.tenantViewId && !remotePreflightReady) return;
        // Local canvases cannot resolve a deleted local kernel, so local and
        // cloud kernels both fail closed while dependents remain. Only the
        // server-issued cloud force-delete capability permits orphaning them.
        if (pendingUsage.length > 0 && !target.canForceDelete) return;
        try {
            if (target.tenantViewId) {
                await deleteCloudTenantView(target, {
                    force: pendingUsage.length > 0,
                });
            }
            else deleteKernel(null, command);
            if (selectedCommand === command) setSelectedCommand(null);
        } catch (error) {
            setLifecycleNotice(lifecycleFailure("Delete kernel", error));
        } finally {
            // A failed CAS/API request should not strand an obsolete confirm
            // dialog. The selected kernel itself stays open for a fresh retry.
            setPendingDelete(null);
        }
    }, [
        kernels,
        pendingDelete,
        pendingUsage,
        remotePreflightReady,
        selectedCommand,
        setSelectedCommand,
    ]);

    // Fork a (read-only built-in, or any) kernel into a mutable user copy and
    // jump to the copy so the user can edit it immediately.
    const onFork = useCallback(async (command) => {
        const target = kernels.find((kernel) => kernel.command === command);
        if (!target || !getKernelAffordances(target).canFork) return;
        if (!beginLifecycle("fork")) return;
        try {
            const copy = target.tenantViewId || target.builtin
                ? await forkCloudTenantView(target)
                : await forkKernelCloudFirst(null, command);
            if (copy) navigateTo("kernels", copy.command);
        } catch (error) {
            setLifecycleNotice(lifecycleFailure("Fork kernel", error));
        } finally {
            finishLifecycle();
        }
    }, [beginLifecycle, finishLifecycle, kernels, navigateTo]);

    const onTogglePublish = useCallback(async (kernel) => {
        if (!kernel?.tenantViewId) return;
        const target = kernels.find(
            (candidate) => candidate.tenantViewId === kernel.tenantViewId,
        );
        if (!target) return;
        const action = target.visibility === "published" ? "unpublish" : "publish";
        if (action === "unpublish") {
            if (!readBoolean(target, "canUnpublish", "can_unpublish")) return;
        } else if (!getKernelAffordances(target).canPublish) {
            return;
        }
        if (!beginLifecycle(action)) return;
        try {
            if (action === "unpublish") {
                const updated = await unpublishCloudTenantView(target);
                const count = updated.visibleDependents?.length || 0;
                setLifecycleNotice(count
                    ? `Kernel unpublished. ${count} published canvas${count === 1 ? "" : "es"} can no longer resolve it.`
                    : "Kernel unpublished.");
            } else {
                assertKernelScopePublishable(target);
                const inspection = await inspectTenantViewSecretFindings(
                    target.tenantViewId,
                );
                if (
                    target.version != null
                    && inspection.version !== String(target.version)
                ) {
                    throw new Error("The kernel changed. Refresh and review it before publishing.");
                }
                if (inspection.totalFindingCount > 0) {
                    if (inspection.findingsTruncated) {
                        throw new Error(
                            "Publishing is blocked because the secret-finding list is truncated.",
                        );
                    }
                    if (!target.canOverrideSecrets) {
                        throw new Error(
                            "Publishing is blocked by secret-like content; tenant management permission is required to review false positives.",
                        );
                    }
                    setPendingSecretPublish({ target, inspection });
                } else {
                    await publishCloudTenantView(target);
                    setLifecycleNotice("Kernel published.");
                }
            }
        } catch (error) {
            setLifecycleNotice(error instanceof KernelPublishabilityError
                ? error.message
                : lifecycleFailure(
                    target.visibility === "published" ? "Unpublish kernel" : "Publish kernel",
                    error,
                ));
        } finally {
            finishLifecycle();
        }
    }, [beginLifecycle, finishLifecycle, kernels]);
    const confirmSecretPublish = useCallback(async (secretOverrides) => {
        const pending = pendingSecretPublish;
        if (!pending || !beginLifecycle("publish")) return;
        try {
            await publishCloudTenantView(pending.target, { secretOverrides });
            setPendingSecretPublish(null);
            setLifecycleNotice("Kernel published.");
        } catch (error) {
            setLifecycleNotice(lifecycleFailure("Publish kernel", error));
        } finally {
            finishLifecycle();
        }
    }, [beginLifecycle, finishLifecycle, pendingSecretPublish]);
    const onHide = useCallback(async (kernel) => {
        const target = kernels.find(
            (candidate) => candidate.tenantViewId === kernel?.tenantViewId,
        );
        if (!target?.canHide) return;
        if (!beginLifecycle("hide")) return;
        try {
            const updated = await hideCloudTenantView(target);
            const count = updated.visibleDependents?.length || 0;
            setLifecycleNotice(count
                ? `Official kernel hidden. ${count} published canvas${count === 1 ? "" : "es"} can no longer resolve it.`
                : "Official kernel hidden.");
        } catch (error) {
            setLifecycleNotice(lifecycleFailure("Hide official kernel", error));
        } finally {
            finishLifecycle();
        }
    }, [beginLifecycle, finishLifecycle, kernels]);

    if (!selectedKernel) {
        return (
            <>
                <TenantViewLifecycleNotice
                    message={lifecycleNotice}
                    onDismiss={() => setLifecycleNotice(null)}
                />
                <StudioOverview
                    kernels={kernels}
                    canvases={canvases}
                    onOpenKernel={(cmd) => navigateTo("kernels", cmd)}
                    onOpenCanvas={(slug) => navigateTo("canvases", slug)}
                />
                <HiddenOriginDeploymentViews kind="kernel" />
                {pendingSecretPublish ? (
                    <SecretFindingOverrideDialog
                        title="Review kernel secret findings"
                        findings={pendingSecretPublish.inspection.findings}
                        contentDigest={pendingSecretPublish.inspection.contentDigest}
                        detectorVersion={pendingSecretPublish.inspection.detectorVersion}
                        busy={lifecycleBusy === "publish"}
                        onCancel={() => setPendingSecretPublish(null)}
                        onConfirm={confirmSecretPublish}
                    />
                ) : null}
            </>
        );
    }

    if (selectedKernel.tenantViewId && selectedKernel.bodyLoaded !== true) {
        return (
            <div
                className="flex-1 grid place-items-center text-[12px] text-[color:var(--muted)]"
                aria-busy={bodyLoadError ? undefined : "true"}
                role={bodyLoadError ? "alert" : "status"}
            >
                {bodyLoadError
                    ? `Could not load kernel: ${bodyLoadError.message || bodyLoadError}`
                    : "Loading kernel…"}
            </div>
        );
    }

    const affordances = getKernelAffordances(selectedKernel);

    return (
        <>
            <TenantViewLifecycleNotice
                message={lifecycleNotice}
                onDismiss={() => setLifecycleNotice(null)}
            />
            {pendingSecretPublish ? (
                <SecretFindingOverrideDialog
                    title="Review kernel secret findings"
                    findings={pendingSecretPublish.inspection.findings}
                    contentDigest={pendingSecretPublish.inspection.contentDigest}
                    detectorVersion={pendingSecretPublish.inspection.detectorVersion}
                    busy={lifecycleBusy === "publish"}
                    onCancel={() => setPendingSecretPublish(null)}
                    onConfirm={confirmSecretPublish}
                />
            ) : null}
            <div className="flex-1 min-h-0 min-w-0 -mx-[var(--main-inset)]">
                <KernelDetailSplit
                    key={selectedKernel.id}
                    kernel={selectedKernel}
                    focusHeader={focusHeader}
                    affordances={affordances}
                    onDelete={affordances.canDelete ? () => setPendingDelete(selectedKernel.command) : undefined}
                    onFork={affordances.canFork ? () => onFork(selectedKernel.command) : undefined}
                    onTogglePublish={
                        selectedKernel.tenantViewId
                        && (selectedKernel.visibility === "published"
                            ? readBoolean(selectedKernel, "canUnpublish", "can_unpublish")
                            : affordances.canPublish)
                            ? () => onTogglePublish(selectedKernel)
                            : undefined
                    }
                    onHide={selectedKernel.canHide
                        ? () => onHide(selectedKernel)
                        : undefined}
                    lifecycleBusy={lifecycleBusy}
                    onOpenCanvas={(slug) => navigateTo("canvases", slug)}
                />
            </div>
            <ConfirmDialog
                open={pendingDelete != null}
                analyticsId={DIALOG.KERNEL_DELETE}
                eyebrow="Kernel"
                title="Delete saved kernel?"
                body={pendingDelete ? (
                    <div className="grid gap-[8px] text-[12px] leading-[1.45] text-[color:var(--muted)]">
                        <p className="m-0">
                            This will permanently delete <span className="font-mono text-[color:var(--ink)]">/{pendingDelete}</span> from
                            your saved kernels.
                        </p>
                        {pendingTarget?.tenantViewId
                            && (!remotePreflightIsCurrent || remoteUsagePreflight.status === "loading") ? (
                            <p className="m-0">Checking dependent canvases…</p>
                        ) : null}
                        {pendingTarget?.tenantViewId
                            && remotePreflightIsCurrent
                            && remoteUsagePreflight.status === "error" ? (
                            <div className="grid gap-[6px]">
                                <p className="m-0" role="alert">
                                    Could not check dependent canvases. Deletion is disabled until the check succeeds.
                                </p>
                                <button
                                    type="button"
                                    className="justify-self-start text-[11px] underline"
                                    onClick={() => setPreflightAttempt((value) => value + 1)}
                                >
                                    Retry dependency check
                                </button>
                            </div>
                        ) : null}
                        {pendingUsage.length > 0 ? (
                            <p className="m-0">
                                Referenced by{" "}
                                {pendingUsage.length} saved canvas{pendingUsage.length === 1 ? "" : "es"}
                                {pendingTarget?.canForceDelete
                                    ? ". Deleting it will leave a missing-kernel placeholder on those canvases."
                                    : ". Remove those references before deleting this kernel."}
                            </p>
                        ) : null}
                    </div>
                ) : null}
                confirmLabel="Delete"
                confirmDisabled={Boolean(
                    (pendingTarget?.tenantViewId && !remotePreflightReady)
                    || (pendingUsage.length > 0 && !pendingTarget?.canForceDelete),
                )}
                destructive
                onConfirm={confirmDelete}
                onCancel={() => setPendingDelete(null)}
            />
        </>
    );
}

// ── Detail metadata (right column) ───────────────────────────────
//
// The kernel render itself is rendered by the parent (left column).
// This column owns the metadata fields, cross-references, and JSON
// inspector that previously lived alongside the render in one
// vertical stack.

// ── Detail-column container ──────────────────────────────────────
//
// Owns the right-column tab state (details / cards / raw) so all
// three views share one tab strip. The KernelEditor stays mounted
// across tab switches via the hidden-display trick — that way the
// in-progress draft + parse state survives jumping over to the
// read-only Details tab and back.

export function KernelDetailSplit({
    kernel,
    focusHeader,
    affordances = getKernelAffordances(kernel),
    onDelete,
    onFork,
    onTogglePublish,
    onHide,
    lifecycleBusy,
    onOpenCanvas,
}) {
    const { canEdit, canDelete, canFork } = affordances;
    const [tab, setTab] = useState("details"); // "details" | "cards" | "raw" | "chat"
    // Reset to Details whenever the user opens a different kernel —
    // landing on the read-only view is the least surprising default.
    useEffect(() => { setTab("details"); }, [kernel.id]);

    // Lifted draft state — shared by the cards/raw editor and the
    // chat. The agent's tool calls mutate this via setDraft and the
    // editor updates in real time on the user's screen. Sync to the
    // kernel prop ref so post-save refreshes (kernelStore notifies →
    // KernelsPage re-renders with a fresh kernel object) reset the
    // draft to match the persisted state.
    const [draft, setDraft] = useState(kernel);
    // Only resync the draft when the PERSISTED kernel's content actually
    // changes — a different kernel opened, or this one saved. The kernel
    // store fires notify() on every save/delete (including unrelated
    // kernels), handing us a fresh `kernel` object even when this kernel's
    // content is unchanged. Resetting on that bare ref churn wiped the
    // user's unsaved raw/card edits — they'd edit, the churn reverted the
    // draft to the stored original, and Save then persisted the original
    // ("edits fully lost"). Compare by content so a ref churn is a no-op.
    const syncedKernelRef = useRef(kernel);
    useEffect(() => {
        if (tenantViewDefinitionDiffers(kernel, syncedKernelRef.current)) {
            setDraft(kernel);
        }
        syncedKernelRef.current = kernel;
    }, [kernel]);

    // KernelWidget re-runs when its `kernel` object changes, so debouncing a
    // separate version while passing the live draft still fired immediately
    // on every keystroke and then fired a duplicate on the trailing edge.
    // Pass one debounced snapshot instead: the editor remains live, while the
    // executable definition changes once after a 500 ms quiet period.
    const [previewDraft] = useDebouncedValue(draft, 500);
    const savedResolvedDefinition = useMemo(
        () => kernelResolverForTenant(null).resolveById(kernel.id),
        [kernel],
    );
    const previewRunContext = useMemo(() => {
        const metadata = { surface: RUN_SURFACES.EDITOR };
        if (canEdit) return createLiveRunContext(metadata);
        if (kernel.tenantViewId) return createStoredRunContext(metadata);
        if (affordances.isBuiltin) return createBundledRunContext(metadata);
        return createUnknownRunContext(metadata);
    }, [affordances.isBuiltin, canEdit, kernel.tenantViewId]);
    const previewResolvedDefinition = useMemo(
        () => canEdit
            ? mapResolvedKernel(savedResolvedDefinition, previewDraft)
            : savedResolvedDefinition,
        [canEdit, previewDraft, savedResolvedDefinition],
    );
    const modelRunContext = useMemo(
        () => taintRunContext(
            previewRunContext,
            savedResolvedDefinition?.definitionSource,
        ),
        [previewRunContext, savedResolvedDefinition],
    );
    const previewUsesPersistedBody = useMemo(
        () => !tenantViewDefinitionDiffers(previewDraft, kernel),
        [previewDraft, kernel],
    );
    const reportRunOutcome = useCallback((event) => {
        if (
            !previewUsesPersistedBody
            || !isStoredTenantViewBody(kernel)
            || !kernel.tenantViewId
        ) return;
        void recordTenantViewRun(kernel.tenantViewId, {
            outcome: event.outcome,
            reasonCategory: event.reasonCategory,
            analyticsQueryCount: event.analyticsQueryCount ?? 0,
        }).catch((error) => {
            console.warn("[kernels] run telemetry failed", error);
        });
    }, [kernel, previewUsesPersistedBody]);

    // Inline save / revert — available both from the editor's own
    // button row and from the corner badge overlaid on the live
    // preview. Same draft, same behavior.
    //
    // Dirty is computed from the editable definition rather than lifecycle
    // metadata or object identity. The kernel store fires its listener on any
    // save / delete, so the `kernel` prop ref can churn even when
    // the content hasn't changed for this kernel — a ref-equality
    // check would leave the badge stuck visible after a revert if
    // the kernel ref had refreshed in the meantime.
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [saveWarning, setSaveWarning] = useState(null);
    const [saveRecoveryPending, setSaveRecoveryPending] = useState(false);
    const dirty = useMemo(
        () => tenantViewDefinitionDiffers(draft, kernel),
        [draft, kernel],
    );
    const persistDraft = useCallback(async (next) => {
        const saved = kernel.tenantViewId
            ? await updateCloudTenantView(kernel, next)
            : await saveKernelCloudFirst(null, next);
        if (saved?.cloudSaveStatus) {
            throw new Error(saved.cloudSaveMessage || "This kernel is still stored locally.");
        }
        const durabilityWarning = saved?.localDurabilityWarning || null;
        setSaveRecoveryPending(Boolean(durabilityWarning));
        setSaveWarning(
            durabilityWarning
            || formatKernelShareWarnings(kernelShareWarnings(next)),
        );
        return saved;
    }, [kernel]);
    const handleSave = useCallback(async () => {
        if (!canEdit || saving || (!dirty && !saveRecoveryPending)) return;
        setSaving(true);
        try {
            await persistDraft(draft);
            setSaveError(null);
            // The kernel prop refreshes via the kernelStore
            // subscription in KernelsPage, propagating a new kernel
            // ref down — the sync effect above resets `draft` to
            // match, and the content-dirty check flips false.
        } catch (err) {
            setSaveError(err?.message || String(err));
        } finally {
            setSaving(false);
        }
    }, [canEdit, draft, persistDraft, saveRecoveryPending, saving, dirty]);
    const handleRevert = useCallback(() => {
        if (!canEdit || !dirty || saving) return;
        setDraft(kernel);
        setSaveError(null);
        if (!saveRecoveryPending) setSaveWarning(null);
    }, [canEdit, kernel, dirty, saveRecoveryPending, saving]);

    // The server decides row actions. Any row without edit authority receives
    // the same Details-only treatment as a bundled kernel; fork and delete are
    // independently controlled because managers/owners need not share them.
    const detailTabs = canEdit
        ? [
            { id: "details", label: "Details",   active: tab === "details", dot: tab === "details" ? "bg-[color:var(--green)]" : undefined, onSelect: () => setTab("details") },
            { id: "cards",   label: "Cells",     active: tab === "cards",   dot: tab === "cards"   ? "bg-[color:var(--green)]" : undefined, onSelect: () => setTab("cards") },
            { id: "raw",     label: "Raw JSON",  active: tab === "raw",     dot: tab === "raw"     ? "bg-[color:var(--green)]" : undefined, onSelect: () => setTab("raw") },
            { id: "chat",    label: "Chat",      active: tab === "chat",    dot: tab === "chat"    ? "bg-[color:var(--green)]" : undefined, onSelect: () => setTab("chat") },
        ]
        : [
            { id: "details", label: "Details", active: true, dot: "bg-[color:var(--green)]", onSelect: () => setTab("details") },
        ];
    const detailHeader = <TabStrip tabs={detailTabs} className="pt-0" />;

    return (
        <SplitPage
            defaultDetailRatio={0.42}
            focusHeader={focusHeader}
            leftContent={() => (
                // The badge mirrors the PNG button's chrome but
                // sits opposite (top-left), is always visible while
                // dirty, and exposes save / revert directly so the
                // user doesn't need to round-trip through the Cells
                // tab. data-export-ignore keeps it out of PNG
                // exports, same as the PNG button itself.
                <div className="flex h-full min-h-0 flex-col pt-[14px] pb-[18px]">
                    {saveWarning ? (
                        <div
                            data-export-ignore="true"
                            role={saveRecoveryPending ? "alert" : "status"}
                            className="mx-[4px] mb-[6px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[8px] py-[6px] text-[10px] leading-[1.4] text-[color:var(--muted)]"
                        >
                            {saveWarning}
                            {saveRecoveryPending ? (
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="ml-[8px] cursor-pointer bg-transparent uppercase tracking-[0.08em] text-[color:var(--ink)] underline underline-offset-2 disabled:cursor-wait disabled:opacity-50"
                                >
                                    {saving ? "Retrying…" : "Retry local recovery"}
                                </button>
                            ) : null}
                        </div>
                    ) : null}
                    <ExportableBlock
                        filenamePrefix={`kernel-${kernel.command}`}
                        className="flex flex-1 min-h-0 flex-col"
                    >
                        {canEdit && (dirty || saveRecoveryPending) ? (
                            <div
                                data-export-ignore="true"
                                className="absolute top-[4px] left-[4px] z-20 flex items-stretch gap-[2px]"
                            >
                                <span
                                    className="inline-flex items-center border border-[color:var(--line)] bg-[color:var(--bg)] px-[6px] text-[9px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)]"
                                    title={saveError || (
                                        saveRecoveryPending
                                            ? "Cloud save committed; local recovery is pending"
                                            : "Live preview of unsaved edits"
                                    )}
                                >
                                    <span className="mr-[5px] inline-block h-[5px] w-[5px] rounded-full bg-[color:var(--green)]" aria-hidden="true" />
                                    {saveRecoveryPending ? "recovery" : "draft"}
                                </span>
                                <button
                                    type="button"
                                    onClick={handleSave}
                                    disabled={saving}
                                    title={saveError || "Save kernel changes"}
                                    className="cursor-pointer border border-[color:var(--line)] bg-[color:var(--bg)] px-[6px] text-[9px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] disabled:opacity-50 disabled:cursor-wait"
                                >
                                    {saving ? "…" : saveRecoveryPending ? "retry" : "save"}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleRevert}
                                    disabled={saving || !dirty}
                                    title="Discard unsaved changes"
                                    className="cursor-pointer border border-[color:var(--line)] bg-[color:var(--bg)] px-[6px] text-[9px] uppercase tracking-[0.14em] text-[color:var(--muted-soft)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    revert
                                </button>
                            </div>
                        ) : null}
                        <KernelWidget
                            kernel={previewDraft}
                            resolvedDefinition={previewResolvedDefinition}
                            runContext={previewRunContext}
                            onRunOutcome={reportRunOutcome}
                            fill={true}
                            showHeader={false}
                        />
                    </ExportableBlock>
                </div>
            )}
            rightHeader={detailHeader}
            rightContent={() => (
                // Single mount tree across all tabs so the draft +
                // parse state survives every tab switch. Each tab's
                // wrapper toggles via `display` so the underlying
                // React subtrees stay alive. Chat + Editor share the
                // lifted `draft` / `setDraft` so the agent's tool
                // calls mutate state the editor renders, and switching
                // tabs is instant.
                <div className="flex h-full min-h-0 min-w-0 flex-col">
                    <div
                        className="flex-1 min-h-0 overflow-y-auto"
                        style={{ display: tab === "details" ? "block" : "none" }}
                    >
                        <KernelDetailMetadata
                            kernel={kernel}
                            affordances={affordances}
                            onDelete={onDelete}
                            onFork={onFork}
                            onTogglePublish={onTogglePublish}
                            onHide={onHide}
                            lifecycleBusy={lifecycleBusy}
                            onOpenCanvas={onOpenCanvas}
                        />
                    </div>
                    {canEdit ? (
                        <>
                            <div
                                className="flex-1 min-h-0 flex-col"
                                style={{ display: (tab === "cards" || tab === "raw") ? "flex" : "none" }}
                            >
                                <KernelEditor
                                    kernel={kernel}
                                    draft={draft}
                                    setDraft={setDraft}
                                    view={tab === "raw" ? "raw" : "cards"}
                                    onSave={persistDraft}
                                    saveNotice={saveWarning}
                                />
                            </div>
                            <div
                                className="flex-1 min-h-0 flex-col"
                                style={{ display: tab === "chat" ? "flex" : "none" }}
                            >
                                <KernelChat
                                    kernel={kernel}
                                    draft={draft}
                                    setDraft={setDraft}
                                    resolvedDefinition={savedResolvedDefinition}
                                    runContext={modelRunContext}
                                />
                            </div>
                        </>
                    ) : null}
                </div>
            )}
        />
    );
}

function KernelDetailMetadata({
    kernel,
    affordances = getKernelAffordances(kernel),
    onDelete,
    onFork,
    onTogglePublish,
    onHide,
    lifecycleBusy,
    onOpenCanvas,
}) {
    const { isBuiltin, canEdit, canDelete, canFork } = affordances;
    const stepCount = Array.isArray(kernel.steps) ? kernel.steps.length : 0;
    const paramKeys = kernel.params && typeof kernel.params === "object" ? Object.keys(kernel.params) : [];
    const usedIn = findCanvasesUsingKernel(null, kernel.command);

    return (
        <div className="grid gap-[14px] min-w-0 [overflow-wrap:anywhere] pt-[14px] pb-[18px]">
            {!canEdit ? (
                <div className="grid gap-[4px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[10px] py-[8px]">
                    <span className="inline-flex w-fit items-center rounded-[2px] border border-[color:var(--line)] px-[5px] py-[1px] text-[9px] uppercase tracking-[0.08em] text-[color:var(--muted)]">
                        {isBuiltin ? "Built-in · read-only" : "Shared · read-only"}
                    </span>
                    <span className="text-[11px] leading-[1.4] text-[color:var(--muted)]">
                        {isBuiltin
                            ? "Shipped with the dashboard. Fork it to get an editable copy under a new command."
                            : canFork
                                ? "Published by a teammate. Fork it to get an editable copy under a new command."
                                : "Your current access permits viewing this kernel only."}
                    </span>
                </div>
            ) : null}
            <div className="grid gap-[10px] grid-cols-1">
                <DetailField label="Command" value={`/${kernel.command}`} mono />
                <DetailField label="Title" value={kernel.title} />
                <DetailField label="Steps" value={String(stepCount)} />
                <DetailField label="Render" value={kernel.render?.kind || ""} mono />
                <DetailField label="Params" value={paramKeys.join(", ")} />
                <DetailField label="Updated" value={kernel.updatedAt ? new Date(kernel.updatedAt).toLocaleString() : ""} />
                {kernel.description ? (
                    <DetailField label="Description" value={kernel.description} />
                ) : null}
            </div>
            <div className="grid gap-[6px]">
                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>Used in canvases</span>
                {usedIn.length === 0 ? (
                    <span className="text-[11px] text-[color:var(--muted-soft)]">
                        {kernel.tenantViewId
                            ? "Cloud references are checked before deletion."
                            : "Not referenced by any saved canvas."}
                    </span>
                ) : (
                    <div className="flex flex-wrap items-baseline gap-[6px]">
                        {usedIn.map((c) => (
                            <button
                                key={c.id}
                                type="button"
                                onClick={() => onOpenCanvas?.(c.slug)}
                                className="cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-transparent px-[6px] py-[1px] font-mono text-[10.5px] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] [overflow-wrap:anywhere] text-left"
                            >
                                /{c.slug}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            {canFork || canDelete || onTogglePublish || onHide ? (
                <div className="flex items-baseline gap-[8px] border-t border-[color:var(--line-soft)] pt-[10px]">
                    {canFork ? (
                    <button
                        type="button"
                        onClick={onFork}
                        disabled={lifecycleBusy != null}
                        aria-busy={lifecycleBusy === "fork" ? "true" : undefined}
                        className="cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] py-[3px] text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] disabled:cursor-wait disabled:opacity-60"
                    >
                        {lifecycleBusy === "fork" ? "Forking…" : "Fork to customize"}
                    </button>
                    ) : null}
                    {canDelete ? (
                    <button
                        type="button"
                        onClick={onDelete}
                        className="cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] py-[3px] text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:border-[color:var(--red,#c0392b)] hover:text-[color:var(--red,#c0392b)]"
                    >
                        Delete kernel
                    </button>
                    ) : null}
                    {onTogglePublish ? (
                    <button
                        type="button"
                        onClick={onTogglePublish}
                        disabled={lifecycleBusy != null}
                        aria-busy={
                            lifecycleBusy === "publish" || lifecycleBusy === "unpublish"
                                ? "true"
                                : undefined
                        }
                        className="cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] py-[3px] text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] disabled:cursor-wait disabled:opacity-60"
                    >
                        {lifecycleBusy === "publish"
                            ? "Publishing…"
                            : lifecycleBusy === "unpublish"
                                ? "Unpublishing…"
                                : kernel.visibility === "published"
                                    ? "Unpublish"
                                    : "Publish"}
                    </button>
                    ) : null}
                    {onHide ? (
                    <button
                        type="button"
                        onClick={onHide}
                        disabled={lifecycleBusy != null}
                        aria-busy={lifecycleBusy === "hide" ? "true" : undefined}
                        className="cursor-pointer rounded-[2px] border border-[color:var(--line)] bg-transparent px-[8px] py-[3px] text-[10px] uppercase tracking-[0.04em] text-[color:var(--muted)] hover:border-[color:var(--line-strong)] hover:text-[color:var(--ink)] disabled:cursor-wait disabled:opacity-60"
                    >
                        {lifecycleBusy === "hide" ? "Hiding…" : "Hide official kernel"}
                    </button>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}

function DetailField({ label, value, mono = false }) {
    return (
        <div className="grid gap-[2px] min-w-0">
            <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.06em]`}>{label}</span>
            <span
                className={`text-[12px] leading-[1.32] text-[color:var(--ink)] break-words ${mono ? "font-mono" : ""}`}
                title={typeof value === "string" ? value : undefined}
            >
                {value || <span className="text-[color:var(--muted-soft)]">—</span>}
            </span>
        </div>
    );
}
